// invite_hub_tool: authoritative Invite Hub attribution on the enrollment funnel
// (PT-4138). Derived from the enrollment link's UTM params on landing, then kept in
// sessionStorage so every funnel step — including enrollment_completed, which fires
// after the params are long gone from the URL — reports the same value.

const INVITE_HUB_TOOLS = [
    'email_send',
    'email_reminder',
    'send_on_own',
    'qr_poster',
    'invite_link',
] as const;

export type InviteHubTool = (typeof INVITE_HUB_TOOLS)[number];

const STORAGE_KEY = 'rr_invite_hub_tool';

const isInviteHubTool = (value: string | null): value is InviteHubTool =>
    Boolean(value) && (INVITE_HUB_TOOLS as readonly string[]).includes(value as string);

export const getInviteHubToolFromSearch = (search: string): InviteHubTool | null => {
    const params = new URLSearchParams(search);
    if (params.get('utm_campaign') !== 'invite_hub_invite_tools') {
        return null;
    }
    const content = params.get('utm_content');
    if (isInviteHubTool(content)) {
        return content;
    }
    switch (params.get('utm_medium')) {
        case 'invite_link':
            return 'invite_link';
        case 'qr_code':
            return 'qr_poster';
        default:
            return null;
    }
};

// Reads the current URL first so a fresh Invite Hub link always wins, then falls
// back to the value persisted earlier in the session. Null when neither exists.
export const getInviteHubTool = (): InviteHubTool | null => {
    try {
        const fromUrl = getInviteHubToolFromSearch(window.location.search);
        if (fromUrl) {
            sessionStorage.setItem(STORAGE_KEY, fromUrl);
            return fromUrl;
        }
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return isInviteHubTool(stored) ? stored : null;
    } catch {
        return null;
    }
};

// Attribution is consumed once the funnel completes; without this, a second
// enrollment started in the same tab would still report the first one's tool.
export const clearInviteHubTool = () => {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // sessionStorage unavailable — nothing to clear.
    }
};
